/**
 * A que punto se ha llegado cuando hay varios cerca.
 *
 * EL PROBLEMA, MEDIDO
 *   En Hostafrancs hay seis pares de puntos cuyos radios se tocan: Placa de
 *   Joan Corrades y Carretera de la Bordeta estan a 27 metros con radios de 25
 *   cada uno. En la franja intermedia se esta dentro de los dos a la vez.
 *
 *   La invariante del proyecto ("estando ENCIMA de un punto no se puede estar
 *   dentro del radio de otro") se cumple, y eso garantiza que el punto correcto
 *   SIEMPRE es alcanzable. Lo que no resuelve es que, a medio camino, haya dos
 *   candidatos.
 *
 *   Hasta el 11/09/2026 el cliente registraba visita en TODOS los puntos del
 *   radio y abria la ficha del que contestara antes el servidor: no el mas
 *   cercano, el que ganara la carrera. Arbitrario, y ademas desbloqueaba
 *   preguntas de sitios que la persona no estaba mirando.
 *
 * LO QUE HACE AHORA
 *   Gana el mas cercano, que es casi siempre el que se tiene delante. Solo
 *   cuando el segundo esta a menos de `MARGEN_EMPATE_METROS` se considera que
 *   hay empate de verdad y se devuelve la lista para que decida la persona.
 *
 *   Por que no preguntar siempre: en la calle, con prisa y con el movil en una
 *   mano, una pregunta que se podia haber resuelto sola es una molestia. Y por
 *   que no decidir siempre: con dos puntos a la misma distancia, elegir por
 *   centimetros de GPS es elegir a cara o cruz.
 *
 * POR QUE AQUI Y NO EN EL HOOK
 *   Es logica de negocio pura, se prueba sin navegador, y el dia que el servidor
 *   necesite la misma decision (por ejemplo para validar una visita ambigua) no
 *   habra que reescribirla.
 */

import { distanciaEnMetros, type Coordenada } from './geo.ts';

/**
 * Diferencia por debajo de la cual dos puntos se consideran igual de cerca.
 *
 * Diez metros: por debajo de eso, el error tipico del GPS urbano (entre 10 y 40
 * metros) es mayor que la diferencia, asi que "el mas cercano" no significa
 * nada y quien lo sabe de verdad es quien esta alli mirando.
 */
export const MARGEN_EMPATE_METROS = 10;

export interface PuntoConRadio {
  id: number;
  lat: number;
  lon: number;
  radio_metros: number;
}

export interface Llegada<T extends PuntoConRadio> {
  /** El punto al que se ha llegado, o null si hay empate y decide la persona. */
  elegido: T | null;
  /** Los candidatos empatados, ordenados por distancia. Vacio si no hay duda. */
  candidatos: T[];
  /** Todos los que caen dentro del radio, ordenados. Para pintar estados. */
  dentro: T[];
  /** Distancia a cada punto, para la lista y para el aviso de "a X metros". */
  distancias: Map<number, number>;
}

/**
 * Decide a que punto se ha llegado.
 *
 * `excluidos` son los que ya estan visitados o con una peticion en vuelo: no
 * vuelven a contar ni para elegir ni para empatar.
 */
export function clasificarLlegada<T extends PuntoConRadio>(
  puntos: readonly T[],
  posicion: Coordenada,
  excluidos: ReadonlySet<number> = new Set(),
  margen: number = MARGEN_EMPATE_METROS,
): Llegada<T> {
  const distancias = new Map<number, number>();
  const dentro: T[] = [];

  for (const punto of puntos) {
    const distancia = distanciaEnMetros(posicion, { lat: punto.lat, lon: punto.lon });
    distancias.set(punto.id, distancia);
    if (distancia <= punto.radio_metros) dentro.push(punto);
  }

  dentro.sort((a, b) => (distancias.get(a.id) ?? 0) - (distancias.get(b.id) ?? 0));

  const pendientes = dentro.filter((p) => !excluidos.has(p.id));
  if (pendientes.length === 0) {
    return { elegido: null, candidatos: [], dentro, distancias };
  }

  const primero = pendientes[0]!;
  const empatados = pendientes.filter(
    (p) => (distancias.get(p.id) ?? 0) - (distancias.get(primero.id) ?? 0) < margen,
  );

  if (empatados.length > 1) {
    return { elegido: null, candidatos: empatados, dentro, distancias };
  }

  return { elegido: primero, candidatos: [], dentro, distancias };
}
